import React from "react";
import styled, { css } from "styled-components";
import { LangDirectionContext } from "../../../context/LangDirectionContext";

const InputStyled = styled.input`
  outline: none;
  border: none;
  display: block;
  appearance: none;
  position: relative;
  z-index: 2;
  padding: 0;
  transition: 0.3s padding-top ease;
  padding-left: 10px;
  width: 100%;
  font-size: ${({ theme }) => theme.formElements.fontSize};
  line-height: 1.5;
  border-radius: ${({ theme }) => theme.formElements.borderRadius};
  background-color: transparent;
  caret-color: ${({ theme }) => theme.colors.light};
  font-weight: 500;
  height: ${({ theme }) => theme.formElements.height};
  color: ${({ theme }) => theme.formElements.fontColor};

  @media (min-width: ${({ theme }) => theme.viewports.md}) {
    padding-left: 12px;
    ${(props) =>
      props.direction === "rtl" &&
      css`
        padding-right: 12px;
        padding-left: 0;
      `}
  }
  ${(props) =>
    props.direction === "rtl" &&
    css`
      padding-right: 10px;
      padding-left: 0;
    `}
  &:-webkit-autofill {
    -webkit-animation-name: autofill;
    -webkit-animation-fill-mode: both;
  }

  &:focus {
    background: none;
  }

  ~ label {
    will-change: transform;
    display: block;
    width: auto;
    position: absolute;
    z-index: 1;
    bottom: 14px;
    left: 10px;

    @media (min-width: ${({ theme }) => theme.viewports.md}) {
      left: 12px;
      ${(props) =>
        props.direction === "rtl" &&
        css`
          right: 12px;
          left: auto;
        `}
    }

    padding-bottom: 0;
    margin-bottom: 0;
    font-weight: 500;
    line-height: 1.5;
    font-size: 16px;
    transform-origin: 0 50%;
    transform: translateY(0) scale(1) translateZ(0);
    transition: transform 0.3s, color 0.3s;
    color: #5b5b5b;
    ${(props) =>
      props.direction === "rtl" &&
      css`
        right: 10px;
        left: auto;
        transform-origin: 100% 0;
      `}
  }

  &.fill,
  &:focus {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.light};
    padding-top: 20px;
    & + label {
      transform: translateY(-10px) scale(0.8);
      color: #008ac0;
    }
  }

  &:focus {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.light};
    background-color: rgba(0, 185, 255, 0.1);
  }

  &.fill:hover,
  &:hover {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.light};
  }

  &.fill {
    box-shadow: none;
    &:focus {
      box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.light};
      background-color: rgba(0, 185, 255, 0.1);
    }
  }

  &.error {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.formElements.error};
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  ::placeholder {
    color: ${({ theme }) => theme.colors.medGray};
  }

  &.numeric-password {
    font-family: "password-mask", serif;

    @font-face {
      font-family: "password-mask";
      src: url(data:font/woff;charset:utf-8;base64,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)
        format("woff");
    }
  }

  &.numeric-password::placeholder {
    font-family: "Montserrat", serif;
  }

  ${(props) =>
    props.modern &&
    css`
      outline: none;
      border: none;
      display: block;
      appearance: none;
      position: relative;
      z-index: 1;
      padding: 0;
      width: 100%;
      background: none;
      font-size: 16px;
      line-height: 1.5;
      border-radius: 0;
      background-color: transparent;
      caret-color: ${({ theme }) => theme.colors.light};
      font-weight: 500;
      margin: 20px 0 0;
      height: 40px;

      &:focus {
        background: none;
      }

      ~ label {
        will-change: transform;
        display: block;
        position: absolute;
        z-index: 0;
        bottom: 7px;
        left: 0;
        width: 100%;
        padding-bottom: 0;
        margin-bottom: 0;
        font-weight: 500;
        line-height: 1.5;
        font-size: 16px;
        transform-origin: 0 50%;
        transform: translateY(0) scale(1) translateZ(0);
        transition: transform 0.3s, color 0.3s;
        color: #787878;
      }

      ~ hr {
        display: block !important;
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 2px;
        border: 0;
        border-radius: 4px;
        margin: 0;
        background: #cccccc;
        z-index: 1;

        &:after {
          transition: all 250ms ease-out;
          transform: scaleX(0) translateZ(0);
          transform-origin: left top;
          will-change: transform;
          content: "";
          display: block;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 4px;
          background: ${({ theme }) => theme.colors.light};
        }
      }

      &.error {
        box-shadow: none;
        ~ hr:after {
          background-color: ${({ theme }) => theme.formElements.error};
        }
      }

      &.fill,
      &:focus {
        padding: 0;
        ~ hr:after {
          transform: scaleX(1) translateZ(0);
        }
      }

      &.fill,
      &:focus {
        box-shadow: none;
        & + label {
          --label-y: -25px;
          --label-scale: 0.8;
          transform: translateY(-25px) scale(0.8);
          z-index: 2;
        }
      }

      &:focus {
        box-shadow: none;
        background-color: transparent;
      }
      &.fill:hover,
      &:hover {
        box-shadow: none;
      }

      &.fill {
        box-shadow: none;
        &:focus {
          box-shadow: none;
          background-color: transparent;
        }
      }
    `}
`;

export const Input = ({ modern, className, ...props }) => {
  const direction = React.useContext(LangDirectionContext);
  return (
    <InputStyled
      type="text"
      modern={modern}
      {...props}
      className={className}
      direction={direction}
    />
  );
};
